import { Loader2, RefreshCw, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/infra/utils';
import type { OneClickReceipt, OneClickReceiptState } from '@/types/kalshi';
import { formatPriceDollarsAsCents } from './OscarSequenceReceipt';
import {
  LIVE_HOTKEY_ACTION_IDS,
  LIVE_HOTKEY_REGISTRY,
  hotkeyLabelFor,
  supportsShiftChord,
  type LiveHotkeyMap,
  type LiveHotkeyGrants,
} from '@/lib/trading/liveHotkeyRegistry';
import { useLiveHotkeyGrants, useLiveHotkeyMap } from '@/hooks/useLiveHotkeys';

/**
 * #314 — durable one-click receipts.
 *
 * Presentation only. Every field comes from the relay's projection over the
 * trades audit; this component derives nothing about what happened at the
 * venue and never upgrades a state the relay refused to call settled.
 *
 * Price formatting is reused from the #302 card rather than reimplemented —
 * shifting the exact decimal STRING is the only correct transform, and having
 * two copies of that rule is how one of them drifts.
 */

const STATE_LABEL: Record<OneClickReceiptState, string> = {
  rejected: 'Rejected',
  resting: 'Resting',
  // 'Partial fill', not 'Partially filled': the badge is shrink-0, so every
  // extra badge pixel comes out of the room the summary has beside it (#612).
  // Since #760 that no longer costs the summary any of its own text — it takes
  // its own line instead — but it still decides whether a row is one line or
  // two, and a shorter label keeps more of them on one.
  partially_filled: 'Partial fill',
  filled: 'Filled',
  cancelled: 'Cancelled',
  unknown: 'Unknown',
};

const STATE_CLASS: Record<OneClickReceiptState, string> = {
  rejected: 'bg-negative/10 text-negative',
  resting: 'bg-muted text-muted-foreground',
  partially_filled: 'bg-amber-500/10 text-amber-500',
  filled: 'bg-positive/10 text-positive',
  cancelled: 'bg-muted text-muted-foreground',
  // Amber, never neutral: unknown means "go look", not "nothing happened".
  unknown: 'bg-amber-500/10 text-amber-500',
};

function timeLabel(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  return new Date(ms).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/** Fill counts are fractional; render whatever precision is really there
 *  rather than rounding it to a tidier-looking integer. */
function fillLabel(receipt: OneClickReceipt): string | null {
  if (receipt.filledCount <= 0) return null;
  const filled = String(receipt.filledCount);
  return receipt.count == null ? `${filled} filled` : `${filled}/${receipt.count}`;
}

/**
 * Short tag for the LT2 input-method audit stamp. Absent on pre-LT2 rows;
 * unknown strings render verbatim rather than being guessed at.
 *
 * #725 — the stamp names the ACTION (`hotkey-a` = the passive top/left post),
 * so the tag is resolved through the registry rather than by hand. Two
 * consequences worth stating:
 *
 *   • W/E receipts used to render the raw string `hotkey-w`, because only a/d
 *     were mapped. Every order action now has a tag.
 *   • The tag shows the key bound RIGHT NOW. That is deliberate for a live
 *     receipt strip a trader reads mid-session — it answers "which key did
 *     that" in the vocabulary currently on their keyboard. The durable audit
 *     record keeps the stable action id regardless.
 */
function inputMethodTag(
  inputMethod: string | null | undefined,
  map: LiveHotkeyMap,
  grants: LiveHotkeyGrants,
): { tag: string; title: string } | null {
  if (inputMethod == null) return null;
  if (inputMethod === 'mouse') return null; // mouse is the default — no tag
  const action = LIVE_HOTKEY_ACTION_IDS.find(
    (id) => LIVE_HOTKEY_REGISTRY[id].inputMethod === inputMethod,
  );
  if (!action) return { tag: inputMethod, title: `Placed via ${inputMethod}` };
  const meta = LIVE_HOTKEY_REGISTRY[action];
  // ⚠️ The tooltip names the ACTION and marks the key as the CURRENT one.
  //
  // Receipts are durable, so a trader who rebinds later would otherwise read
  // "Placed via hotkey J" against an order actually placed with A — a false
  // statement on a trading surface. The stored stamp records the action, not
  // the keystroke, so naming a past key is something this row genuinely
  // cannot do; saying so is the honest form.
  return {
    tag: hotkeyLabelFor(map, action, grants),
    title: `Placed via the ${meta.label.toLowerCase()} hotkey — currently ${hotkeyLabelFor(map, action, grants)}`,
  };
}

/**
 * #724 — a leg of a two-sided S press says so. The leg's own stamp still names
 * the passive action it IS (hotkey-a / hotkey-d), but read alone it looked
 * like a separate A or D press, and the pair was invisible on the only
 * receipt a trader keeps. The tag names the pair and this leg's place in it;
 * the other leg carries the same press id on its own receipt.
 */
function twoSidedTag(
  group: NonNullable<OneClickReceipt['group']>,
  sizeMultiplier: number | null | undefined,
  map: LiveHotkeyMap,
  grants: LiveHotkeyGrants,
): { tag: string; title: string } {
  const key = hotkeyLabelFor(map, 'two-sided', grants);
  const place = `${group.leg + 1}/${group.of}`;
  const side = group.leg === 0 ? 'top/left' : 'bottom/right';
  // #1067 — a Shift+S leg says so, with its multiple: otherwise a 3× leg of
  // a 100 one-click size reads exactly like a plain S leg at 300. Keyed on
  // the stored stamp; the key shown is today's two-sided key, as for S.
  if (sizeMultiplier != null) {
    // A key that cannot carry Shift (unbound, or remapped to a non-letter
    // since) names no chord: "⇧/" would be a chord that cannot exist.
    const chordKey = key !== '—' && supportsShiftChord(key) ? key : null;
    return {
      tag: `${chordKey == null ? '⇧both' : `⇧${chordKey}`} ${sizeMultiplier}× ${place}`,
      title: `The ${side} leg of one larger post-both-sides press (Shift${
        chordKey == null ? '' : `+${chordKey}`
      }), each side at ${sizeMultiplier}× the one-click size; the other leg has its own receipt.`,
    };
  }
  return {
    tag: `${key === '—' ? 'both' : key} ${place}`,
    title: `The ${side} leg of one post-both-sides press${
      key === '—' ? '' : ` (currently ${key})`
    }; the other leg has its own receipt.`,
  };
}

function ReceiptRow({ receipt }: { receipt: OneClickReceipt }) {
  const price = formatPriceDollarsAsCents(receipt.priceDollars);
  const fills = fillLabel(receipt);
  const hotkeys = useLiveHotkeyMap();
  const grants = useLiveHotkeyGrants();
  const inputTag =
    receipt.group != null
      ? twoSidedTag(receipt.group, receipt.sizeMultiplier, hotkeys, grants)
      : inputMethodTag(receipt.inputMethod, hotkeys, grants);
  return (
    <div data-testid={`one-click-receipt-${receipt.clientOrderId}`}>
      {/* #760 — state on top, summary on its own line beneath it, ALWAYS.
          Nothing is ever truncated or hidden behind hover.

          Measured on the real panel, where the row is 317px:
            384  w-96 rail
             −1  its own border-l
             −8  the w-2 scroll gutter beside the column (GameDetailPanel:2538)
            −32  the px-4 receipts host (:2451)
             −2  this card's border
            −24  this card's px-3
          A summary carrying a fill count is 264.95px, which leaves 44.05px for
          the pill — and the widest state label ("Cancelled") is 53.2px before
          any key tag. Every tagged fill-count row is over, whatever its state:
          the narrowest, `Filled`, still needs 326.68px. So one line is not a
          layout this row can have.

          It stacks UNCONDITIONALLY rather than only when it must, because the
          alternative is a strip that changes shape while a trader reads it: a
          resting order that starts filling gains its `120/250` and would tip
          from one line to two on the spot. Uniform costs ~13.5px per row that
          would have fit, and that height lands below the book (:2453, after
          the orderbooks at :2376), so it takes no ladder rows. */}
      {/* `items-start` so the pill keeps its own width — stretched, its tinted
          background would run the full width of the card and read as a banner
          rather than a badge. */}
      <div className="flex flex-col items-start gap-y-0">
        <span
          data-testid={`one-click-receipt-state-${receipt.clientOrderId}`}
          className={cn(
            'shrink-0 rounded px-1 py-0.5 text-[11px] font-semibold',
            STATE_CLASS[receipt.state],
          )}
        >
          {STATE_LABEL[receipt.state]}
          {inputTag && (
            <span
              className="text-muted-foreground ml-1 font-mono font-normal"
              title={inputTag.title}
            >
              ⌨{inputTag.tag}
            </span>
          )}
        </span>
        {/* Unbroken and unabridged, with no hover fallback: this is the line a
            trader's eye scans down, so the text never wraps mid-line and never
            ellipsises — that would hide reconciliation data (the timestamp
            keeps its seconds deliberately).

            LEFT-aligned, which is what makes the scan work. Every summary
            opens with the same three fields in a mono face, so flush left puts
            side, quantity and price in one column down the card; the fill
            count, which only some rows carry, then varies the tail rather than
            shunting the whole line sideways. Right-aligning instead pinned the
            timestamps and left every row starting at a different x.

            This span used to claim it "must FIT". It did not: measured on the
            production panel at the SHIPPED keys, a `Partial fill ⌨A` row ran
            30.78px past its box and a `Filled ⌨A` row 9.69px, spilling through
            the card's padding and border. It went unseen because the geometry
            probes measure room against the box that CLIPS a surface, and here
            that box is the rail — `overflow-y-auto` on it computes `overflow-x`
            to `auto` (GameDetailPanel:2140) — which the 30.78px overrun stayed
            inside, hidden under the scroll gutter. #725's `⌨Spacebar` added
            46.36px on top and finally crossed it. */}
        <span className="text-foreground whitespace-nowrap text-left font-mono text-[11px] leading-tight">
          {receipt.side ? receipt.side.toUpperCase() : '—'}
          {receipt.count != null ? ` · ${receipt.count}ct` : ''}
          {` @ ${price ?? '—'}`}
          {fills ? ` · ${fills}` : ''}
          {' · '}
          <span className="text-muted-foreground">{timeLabel(receipt.createdAt)}</span>
          {/* A cancellation is reported alongside the fill state, never instead
              of it: a partly-filled order that was then cancelled has two facts
              and collapsing them into one label misstates the position. */}
          {receipt.cancelled && receipt.state !== 'cancelled' && (
            <XCircle
              data-testid={`one-click-receipt-cancelled-${receipt.clientOrderId}`}
              size={11}
              className="text-muted-foreground ml-1 inline align-[-1px]"
            />
          )}
        </span>
      </div>
      {/* Left with the summary above it (#760). This line was `text-right` to
          sit under a right-aligned summary; now that the summary is flush
          left, a right-aligned message would give one receipt two competing
          edges. It still wraps as prose — that treatment is #612's and is
          unchanged. */}
      {receipt.message && (
        <div
          data-testid={`one-click-receipt-message-${receipt.clientOrderId}`}
          className="text-muted-foreground min-w-0 break-words text-left text-[10px] leading-tight"
          title={receipt.message}
        >
          {receipt.message}
        </div>
      )}
    </div>
  );
}

export interface OneClickReceiptsProps {
  receipts: OneClickReceipt[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  /** Set while the one-click grant is still confirming: the card keeps its
   *  place with this reason in the body, and Refresh is disabled. */
  pendingReason?: string;
}

export function OneClickReceipts({ receipts, loading, error, onRefresh, pendingReason }: OneClickReceiptsProps) {
  return (
    <div
      data-testid="one-click-receipts"
      className="border-border space-y-1.5 rounded-lg border px-3 py-2 text-xs"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted-foreground font-semibold uppercase tracking-wide">
          One-click receipts
        </span>
        <div className="flex items-center gap-2">
          {loading && (
            <Loader2 data-testid="one-click-receipts-loading" size={11} className="animate-spin" />
          )}
          <Link to="/trades" className="text-muted-foreground hover:text-foreground transition-colors">
            All trades
          </Link>
          {/* Title on a wrapper: Firefox shows no tooltip on a disabled button. */}
          <span className="inline-flex" title={pendingReason}>
            <button
              type="button"
              onClick={onRefresh}
              disabled={pendingReason != null}
              className="text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw size={11} />
              Refresh
            </button>
          </span>
        </div>
      </div>

      {error && <div className="text-negative">{error}</div>}

      {pendingReason != null && (
        <div data-testid="one-click-receipts-pending" className="text-muted-foreground">
          {pendingReason}
        </div>
      )}

      {pendingReason == null && !error && receipts.length === 0 && (
        <div data-testid="one-click-receipts-empty" className="text-muted-foreground">
          No one-click orders recorded for this market.
        </div>
      )}

      {/* #760 — receipts sit FURTHER apart than the two lines of one receipt,
          which wrap flush together at `gap-y-0`. Rows are ragged by necessity
          (a fill count never fits beside the pill, a bare resting row always
          does), so nothing but spacing says which pill a wrapped summary
          belongs to — and at the old 2px both readings looked equally true. A
          fill count read against the receipt below it is a misreported
          position. */}
      {!error && receipts.length > 0 && (
        <div className="space-y-1">
          {receipts.map((receipt) => (
            <ReceiptRow key={receipt.clientOrderId} receipt={receipt} />
          ))}
        </div>
      )}
    </div>
  );
}
